import { useCallback, useEffect, useRef } from "react";

import { useApp } from "../../../AppProvider";
import { readRawMapUrl, serializeMapUrl } from "./mapUrlState";

const DROPPED_KEY_LABELS = {
  mode: "map mode",
  locType: "location types",
  group: "parameter groups",
  params: "parameters",
  xparams: "parameters",
  telem: "filters",
  active: "filters",
  ccbwqa: "filters",
  flow: "flow",
  analysis: "analysis",
  minRecords: "min records",
  colorBy: "color by",
  loc: "selected location",
  panel: "results panel",
  legend: "legend",
  on: "layers",
  off: "layers",
  opacity: "layer opacity",
  basemap: "basemap",
  map: "map view",
};

const stripQuestionMark = (search) => (search || "").replace(/^\?/, "");

/**
 * Reads the map state from the URL once, when the map mounts
 * The URL is intentionally not re-read afterwards, state flows one way
 * from React to the URL
 * `reportDropped` collects values that could not be applied (stale links),
 * they are shown in a single notice once hydration completes
 */
export const useInitialMapUrl = () => {
  const initialRef = useRef(null);
  if (!initialRef.current) {
    const { raw, dropped } = readRawMapUrl(window.location.search);
    initialRef.current = { raw, dropped: [...dropped] };
  }

  const reportDropped = useCallback((dropped) => {
    if (dropped?.length) initialRef.current.dropped.push(...dropped);
  }, []);

  return {
    raw: initialRef.current.raw,
    getDropped: () => initialRef.current.dropped,
    reportDropped,
  };
};

/**
 * Shows a single notice listing the link settings that were skipped
 * @param {function} getDropped returns the collected dropped values
 * @param {boolean} isHydrated true once every section has been resolved
 */
export const useDroppedUrlNotice = (getDropped, isHydrated) => {
  const { doToast } = useApp();
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (!isHydrated || hasNotifiedRef.current) return;
    hasNotifiedRef.current = true;
    const labels = [
      ...new Set(getDropped().map(({ key }) => DROPPED_KEY_LABELS[key] || key)),
    ];
    if (labels.length) {
      doToast(
        "warning",
        `Some settings in this link are no longer available and were skipped: ${labels.join(
          ", "
        )}`
      );
    }
  }, [isHydrated]); //eslint-disable-line
};

/**
 * Reads the current camera from the map instance, or undefined before load
 */
export const readCamera = (map) => {
  if (!map) return undefined;
  const center = map.getCenter();
  return {
    zoom: map.getZoom(),
    lat: center.lat,
    lng: center.lng,
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };
};

/**
 * Keeps the URL in sync with the map state using `history.replace`
 * Sections of `current` that are undefined are not ready yet, so their
 * existing URL values are kept (see serializeMapUrl)
 * The camera is read directly from the map when it stops moving, so panning
 * does not re-render the page
 * @param {object} options.current memoized map state (without camera)
 * @param {object} options.ctx memoized { filtersData, defaultLayers }
 * @param {object} options.map mapbox map instance
 * @param {boolean} options.enabled disable for embeds where the URL is hidden
 * @returns {function} buildSearch() returns the serialized state for sharing
 */
export const useMapUrlWriter = ({ current, ctx, map, enabled = true }) => {
  // never write map state onto another page's URL during navigation
  const pathnameRef = useRef(window.location.pathname);
  const latestRef = useRef({ current, ctx, map });
  latestRef.current = { current, ctx, map };

  const buildSearch = useCallback((existingSearch) => {
    const { current, ctx, map } = latestRef.current;
    return serializeMapUrl(
      { ...current, camera: readCamera(map) },
      ctx,
      stripQuestionMark(existingSearch)
    );
  }, []);

  const writeUrl = useCallback(() => {
    // the router location can lag behind pushState calls made outside the
    // router, so the browser location is the source of truth
    if (window.location.pathname !== pathnameRef.current) return;
    const existingSearch = stripQuestionMark(window.location.search);
    const nextSearch = buildSearch(existingSearch);
    if (nextSearch !== existingSearch) {
      // replaceState keeps react-router's history entry (state) intact without
      // re-rendering the page on every write. Nothing on the map page reads
      // the search from the router location
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${
          window.location.hash
        }`
      );
    }
  }, [buildSearch]);

  useEffect(() => {
    if (enabled) writeUrl();
  }, [enabled, current, ctx, map, writeUrl]);

  // back/forward (e.g. closing the streamflow dialog) restores an older URL,
  // bring it back in line with the current state
  useEffect(() => {
    if (!enabled) return;
    const handlePopState = () => setTimeout(writeUrl, 0);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [enabled, writeUrl]);

  useEffect(() => {
    if (!enabled || !map) return;
    map.on("moveend", writeUrl);
    return () => {
      map.off("moveend", writeUrl);
    };
  }, [enabled, map, writeUrl]);

  return buildSearch;
};
